import { powersForValue, resolveCrossingSchedule, toSlotRotation } from '@/adapters/primitive/_pure/model/natural/columnArithmetic';
import { deriveNaturalTrace } from '@/adapters/primitive/_pure/model/natural/machine';
import { resolveNaturalSettlementCells } from '@/adapters/primitive/_pure/model/natural/settlementTrace';
import {
    buildFlightWheel,
    buildNaturalPlaceholderWheel,
    buildOperandWheel,
    buildResultWheel,
    buildWholeFlightWheel,
    dimStableWheelRoles,
    toWheelLayout,
    toWheelPosition,
    type PrimitiveWheelLayout,
} from '@/adapters/primitive/_pure/derivation/wheels';
import { toNaturalReceiverLocationPosition } from '@/adapters/primitive/_pure/derivation/natural/locationProjection';
import {
    isSettlementPreviewShown,
    toSettlementResolutionSchedule,
    withSettlementResolution,
    type SettlementPreview,
    type SettlementResolution,
} from '@/adapters/primitive/_pure/derivation/natural/settlementPreview';
import { toShatterOffset, toShatterSpread } from '@/adapters/primitive/_pure/derivation/natural/shatter';
import {
    naturalFlightDurationMs,
    naturalRotationDurationMs,
} from '@/adapters/primitive/_pure/derivation/natural/naturalFlightAnimation';
import { momentKey, momentPart, stepMoment, withMomentFrame } from '@/adapters/primitive/_pure/derivation/primitiveMoment';
import type { Moment } from '@/_contract/_~generated/directionMomentSchema';
import type {
    OperandSchedulingMode,
    PrimitiveOperationDerivationOptions,
} from '@/adapters/primitive/_contract/natural/arithmetic';
import type { NaturalEventEnvelope } from '@/adapters/primitive/_contract/natural/event';
import type { NaturalReceiverLocation } from '@/adapters/primitive/_contract/natural/location';
import type {
    NaturalAssemblyState,
    NaturalMachineState,
    NaturalTrace,
    NaturalTracePoint,
    NaturalContributionTravellerState,
    NaturalCompositeState,
    NaturalOperandWheelTransferState,
} from '@/adapters/primitive/_contract/natural/machine';
import type { NaturalStepDerivationResult } from '@/adapters/primitive/_contract/natural/outcome';
import type {
    Config,
    NaturalOperation,
    PrimitiveWheelRole,
    PrimitiveWheelState,
} from '@/adapters/primitive/_contract/config';
import type { PrimitiveKeyframe, PrimitiveKeyframeTransition, PrimitiveStepState } from '@/adapters/primitive/_contract/steps';

type AdditionSubtractionOperation = Extract<NaturalOperation, 'addition' | 'subtraction'>;

type OperationSpec = {
    firstRole: PrimitiveWheelRole;
    secondRole: PrimitiveWheelRole;
    resultRole: PrimitiveWheelRole;
    firstLabel: string;
    secondLabel: string;
    resultLabel: string;
    firstValue: number;
    secondValue: number;
};

type FlightProjection = {
    contributionTraveller: NaturalContributionTravellerState;
    position: [number, number, number];
    /** The named receiver-relative location currently backing `position`, if any. */
    location: NaturalReceiverLocation | null;
    rotation: number;
    opacity: number;
    /** Band keys the flight renders under; a paid deficit marker keeps travelling as the composite. */
    identity?: { role: PrimitiveWheelRole; variant: 'flight' | 'marker' };
    composed?: boolean;
};

type WheelFlightProjection = {
    operandWheelTransfer: NaturalOperandWheelTransferState;
    position: [number, number, number];
    opacity: number;
};

type AssemblyProjection = {
    assembly: NaturalAssemblyState;
    radixBandPosition: [number, number, number];
    nextBandPosition: [number, number, number];
    radixBandLocation: NaturalReceiverLocation | null;
    nextBandLocation: NaturalReceiverLocation | null;
    radixBandOpacity: number;
    nextBandOpacity: number;
    /** Present only while the assembly enters at its standoff; see `PrimitiveWheelState.entranceGrowth`. */
    entranceGrowth?: number;
};

type CompositeProjection = {
    assembly: NaturalCompositeState;
    /** Operand the composed contributionTraveller came from; its lower bands keep that identity. */
    sourceRole: PrimitiveWheelRole;
    position: [number, number, number];
    location: NaturalReceiverLocation | null;
    outerRotation: number;
    opacity: number;
    /** Non-zero only while the assembly is shattering; index 0 is the innermost lower band. */
    scatter: number;
};

type ProjectionState = {
    machine: NaturalMachineState;
    resultMaterialized: boolean;
    spentBandOpacityByRole: Partial<Record<PrimitiveWheelRole, Partial<Record<number, number>>>>;
    wheelFlight: WheelFlightProjection | null;
    flight: FlightProjection | null;
    assembly: AssemblyProjection | null;
    composite: CompositeProjection | null;
    preview: SettlementPreview | null;
    /** Resolution stage of the current settlement preview. */
    settlementResolution: SettlementResolution | null;
};

function toOperationSpec(args: {
    operation: AdditionSubtractionOperation;
    operandOne: number;
    operandTwo: number;
}): OperationSpec {
    const { operation, operandOne, operandTwo } = args;
    if (operation === 'subtraction') {
        return {
            firstRole: 'minuend',
            secondRole: 'subtrahend',
            resultRole: 'difference',
            firstLabel: 'Minuend',
            secondLabel: 'Subtrahend',
            resultLabel: 'Difference',
            firstValue: operandOne,
            secondValue: operandTwo,
        };
    }

    return {
        firstRole: 'addend-one',
        secondRole: 'addend-two',
        resultRole: 'sum',
        firstLabel: 'AddendOne',
        secondLabel: 'AddendTwo',
        resultLabel: 'Sum',
        firstValue: operandOne,
        secondValue: operandTwo,
    };
}

function toResultWheel(args: {
    layout: PrimitiveWheelLayout;
    operationSpec: OperationSpec;
    machine: NaturalMachineState;
}): PrimitiveWheelState {
    const result = args.machine.wheels.find((wheel) => wheel.role === args.operationSpec.resultRole);
    const bands = result?.bands ?? [];
    return buildResultWheel({
        layout: args.layout,
        role: args.operationSpec.resultRole,
        powers: bands.map((band) => band.power),
        countedThroughDigitByPower: Object.fromEntries(
            bands.map((band) => [band.power, band.countedSlots.length]),
        ),
        countedDigitsByPower: Object.fromEntries(
            bands.map((band) => [band.power, [...band.countedSlots]]),
        ),
    });
}

function toFlightWheel(operationSpec: OperationSpec, flight: FlightProjection): PrimitiveWheelState {
    const value = flight.contributionTraveller.sourceRole === operationSpec.firstRole
        ? operationSpec.firstValue
        : operationSpec.secondValue;
    return { ...buildFlightWheel({
        role: flight.identity?.role ?? flight.contributionTraveller.sourceRole,
        ...(flight.identity === undefined ? {} : { variant: flight.identity.variant }),
        value,
        power: flight.contributionTraveller.band.power,
        position: flight.position,
        rotation: flight.rotation,
        opacity: flight.opacity,
        countedDigits: flight.contributionTraveller.band.countedSlots,
        overhangDigits: flight.contributionTraveller.band.overhangSlots,
        ...(flight.location === null ? {} : { receiverLocation: flight.location }),
    }), helperWheel: {
        key: `contribution:${flight.contributionTraveller.sourceRole}:${flight.contributionTraveller.band.power}`,
        kind: flight.composed || flight.identity?.variant === 'marker' ? 'Composite' : 'Contribution',
    } };
}

function toWholeFlightWheel(operationSpec: OperationSpec, flight: WheelFlightProjection): PrimitiveWheelState {
    return { ...buildWholeFlightWheel({
        role: flight.operandWheelTransfer.sourceRole,
        value: operationSpec.firstValue,
        bands: flight.operandWheelTransfer.bands,
        position: flight.position,
        opacity: flight.opacity,
    }), helperWheel: { key: `contribution:${flight.operandWheelTransfer.sourceRole}:whole`, kind: 'Contribution' } };
}

function toAssemblyWheels(operationSpec: OperationSpec, assembly: AssemblyProjection): PrimitiveWheelState[] {
    const entrance = {
        ...(assembly.entranceGrowth === undefined ? {} : { entranceGrowth: assembly.entranceGrowth }),
        helperWheel: { key: `assembly:${assembly.assembly.nextBand.power}`, kind: 'Assembly' as const },
    };
    return [
        ...(assembly.assembly.radixBand === null ? [] : [{ ...buildFlightWheel({
            variant: 'radix-band',
            role: operationSpec.firstRole,
            value: operationSpec.firstValue,
            power: assembly.assembly.radixBand.power,
            position: assembly.radixBandPosition,
            rotation: 0,
            opacity: assembly.radixBandOpacity,
            countedDigits: assembly.assembly.radixBand.countedSlots,
            overhangDigits: assembly.assembly.radixBand.overhangSlots,
            ...(assembly.radixBandLocation === null ? {} : { receiverLocation: assembly.radixBandLocation }),
        }), ...entrance }]),
        { ...buildFlightWheel({
            variant: 'marker',
            role: operationSpec.resultRole,
            value: 0,
            power: assembly.assembly.nextBand.power,
            position: assembly.nextBandPosition,
            rotation: 0,
            opacity: assembly.nextBandOpacity,
            countedDigits: assembly.assembly.nextBand.countedSlots,
            overhangDigits: assembly.assembly.nextBand.overhangSlots,
            ...(assembly.assembly.nextBand.marker === null ? {} : { mark: assembly.assembly.nextBand.marker }),
            ...(assembly.nextBandLocation === null ? {} : { receiverLocation: assembly.nextBandLocation }),
        }), ...entrance, ...(assembly.assembly.nextBand.marker === 'deficit'
            && (assembly.assembly.radixBand === null || assembly.radixBandOpacity === 0)
            ? { helperWheel: { key: `deficit:${assembly.assembly.nextBand.power}`, kind: 'Deficit-marker' as const } } : {}) },
    ];
}

function toCompositeWheels(args: {
    config: Config;
    operationSpec: OperationSpec;
    propagation: CompositeProjection;
    radix: number;
}): PrimitiveWheelState[] {
    const { config, operationSpec, propagation, radix } = args;
    const spread = toShatterSpread(config);
    const offsetPosition = (memberIndex: number): [number, number, number] => {
        const offset = toShatterOffset({ memberIndex, scatter: propagation.scatter, spread });
        return [
            propagation.position[0] + offset[0],
            propagation.position[1] + offset[1],
            propagation.position[2] + offset[2],
        ];
    };
    const wheels = [
        ...propagation.assembly.lowerBands.map((member, memberIndex) => buildFlightWheel({
            role: propagation.sourceRole,
            value: propagation.sourceRole === operationSpec.firstRole ? operationSpec.firstValue : operationSpec.secondValue,
            power: member.band.power,
            position: offsetPosition(memberIndex),
            rotation: toSlotRotation({ slotOffset: member.slotOffset, radix }),
            opacity: propagation.opacity,
            countedDigits: member.band.countedSlots,
            overhangDigits: member.band.overhangSlots,
            ...(propagation.location === null ? {} : { receiverLocation: propagation.location }),
        })),
        buildFlightWheel({
            role: operationSpec.resultRole,
            value: 1,
            power: propagation.assembly.outerUnit.band.power,
            position: offsetPosition(propagation.assembly.lowerBands.length),
            rotation: propagation.outerRotation,
            opacity: propagation.opacity,
            countedDigits: propagation.assembly.outerUnit.band.countedSlots,
            overhangDigits: propagation.assembly.outerUnit.band.overhangSlots,
            ...(propagation.location === null ? {} : { receiverLocation: propagation.location }),
        }),
    ];
    return wheels.map(wheel => ({
        ...wheel,
        helperWheel: { key: `composite:${propagation.sourceRole}`, kind: 'Composite' },
    }));
}

function snapshotWheels(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    operationSpec: OperationSpec;
    state: ProjectionState;
}): readonly PrimitiveWheelState[] {
    const { config, layout, operationSpec, state } = args;
    return [
        buildOperandWheel({
            layout,
            role: operationSpec.firstRole,
            value: operationSpec.firstValue,
            bandOpacityByPower: { ...state.spentBandOpacityByRole[operationSpec.firstRole] },
        }),
        buildOperandWheel({
            layout,
            role: operationSpec.secondRole,
            value: operationSpec.secondValue,
            bandOpacityByPower: { ...state.spentBandOpacityByRole[operationSpec.secondRole] },
        }),
        state.resultMaterialized
            ? toResultWheel({ layout, operationSpec, machine: state.machine })
            : buildNaturalPlaceholderWheel({ layout, role: operationSpec.resultRole }),
        ...(state.wheelFlight === null ? [] : [toWholeFlightWheel(operationSpec, state.wheelFlight)]),
        ...(state.flight === null ? [] : [toFlightWheel(operationSpec, state.flight)]),
        ...(state.assembly === null ? [] : toAssemblyWheels(operationSpec, state.assembly)),
        ...(state.composite === null
            ? []
            : toCompositeWheels({
                config,
                operationSpec,
                propagation: state.composite,
                radix: state.machine.radix,
            })),
    ];
}

function withoutEntrance(assembly: AssemblyProjection): AssemblyProjection {
    const settled = { ...assembly };
    delete settled.entranceGrowth;
    return settled;
}

function toAssemblyProjection(args: {
    config: Config;
    layout: PrimitiveWheelLayout;
    assembly: NaturalAssemblyState;
    opacity: number;
}): AssemblyProjection {
    const position = toNaturalReceiverLocationPosition({
        config: args.config,
        layout: args.layout,
        location: args.assembly.location,
    });
    return {
        assembly: args.assembly,
        radixBandPosition: position,
        nextBandPosition: position,
        radixBandLocation: args.assembly.location,
        nextBandLocation: args.assembly.location,
        radixBandOpacity: args.opacity,
        nextBandOpacity: args.opacity,
    };
}

export function projectNaturalTraceToStepStates(args: {
    config: Config;
    /** The mode the trace was derived with; composed operations always pass 'grouped'. */
    operandSchedulingMode: OperandSchedulingMode;
    operation: AdditionSubtractionOperation;
    operandOne: number;
    operandTwo: number;
    trace: NaturalTrace;
    dimTerminalOperands?: boolean;
    splitAlignmentApproach?: boolean;
    splitZeroCrossing?: boolean;
}): NaturalStepDerivationResult {
    const { config, operation, trace } = args;
    const layout = toWheelLayout({ config, operandSchedulingMode: args.operandSchedulingMode });
    const operationSpec = toOperationSpec({ operation, operandOne: args.operandOne, operandTwo: args.operandTwo });
    const state: ProjectionState = {
        machine: trace.initialState,
        // A resident PreResult (composed operations) is already a real receiver, not a placeholder.
        resultMaterialized: trace.initialState.wheels
            .find((wheel) => wheel.role === operationSpec.resultRole)
            ?.bands.some((band) => band.countedSlots.length > 0) ?? false,
        spentBandOpacityByRole: {},
        wheelFlight: null,
        flight: null,
        assembly: null,
        composite: null,
        preview: null,
        settlementResolution: null,
    };
    const steps: PrimitiveStepState[] = [];
    let pointIndex = 0;

    const peek = (): NaturalTracePoint | undefined => trace.points[pointIndex];
    const consume = (eventKind?: NaturalEventEnvelope['event']['eventKind']): NaturalTracePoint => {
        const point = trace.points[pointIndex];
        if (point === undefined) throw new Error('Natural presentation exhausted its trace unexpectedly.');
        if (eventKind !== undefined && point.event.event.eventKind !== eventKind) {
            throw new Error(
                `Natural presentation expected ${eventKind} at sequence ${point.event.sequence}, `
                + `received ${point.event.event.eventKind}.`,
            );
        }
        pointIndex += 1;
        state.machine = point.nextState;
        return point;
    };
    const snapshot = (
        transition: PrimitiveKeyframeTransition,
        events: readonly NaturalEventEnvelope[] = [],
    ): PrimitiveKeyframe => {
        const wheels = snapshotWheels({ config, layout, operationSpec, state });
        return {
            wheels: state.preview === null
                ? wheels
                : withSettlementResolution({ config, wheels, preview: state.preview, resolution: state.settlementResolution ?? 'entry' }),
            transition,
            events,
        };
    };
    const pushStep = (
        moment: Moment,
        label: string,
        description: string,
        keyframes: readonly PrimitiveKeyframe[],
    ) => steps.push({ ...momentKey(moment), label, description, keyframes });
    const moveFlight = (
        position: [number, number, number],
        location: NaturalReceiverLocation | null,
        events: readonly NaturalEventEnvelope[] = [],
    ) => {
        const from = state.flight?.position ?? position;
        if (state.flight !== null) state.flight = { ...state.flight, position, location };
        return snapshot({
            durationMs: naturalFlightDurationMs({ config, from, to: position }),
        }, events);
    };
    const flightIdentity = (): { role: PrimitiveWheelRole; variant: 'flight' | 'marker' } => state.flight?.identity
        ?? { role: state.flight?.contributionTraveller.sourceRole ?? operationSpec.firstRole, variant: 'flight' };
    // The next `eventKind` settlement's preview, or null when there is no real receiver to frame against.
    const resolvePreview = (
        eventKind: NaturalEventEnvelope['event']['eventKind'],
        targets: Pick<SettlementPreview, 'incoming' | 'receiver'>,
    ): SettlementPreview | null => {
        const point = trace.points.slice(pointIndex).find((candidate) => candidate.event.event.eventKind === eventKind);
        if (point === undefined) return null;
        const preview: SettlementPreview = { settlements: resolveNaturalSettlementCells(point), ...targets };
        return isSettlementPreviewShown({ config, wheels: snapshotWheels({ config, layout, operationSpec, state }), preview })
            ? preview
            : null;
    };
    const amendLastStep = (amend: (keyframes: readonly PrimitiveKeyframe[]) => PrimitiveKeyframe[]) => {
        const lastStep = steps[steps.length - 1];
        steps[steps.length - 1] = { ...lastStep, keyframes: amend(lastStep.keyframes) };
    };
    const carryAssemblyIncoming = (index: number): { role: PrimitiveWheelRole; variant: 'radix-band' | 'marker' } => (
        index === 0
            ? { role: operationSpec.firstRole, variant: 'radix-band' }
            : { role: operationSpec.resultRole, variant: 'marker' }
    );
    const endPreview = () => {
        state.preview = null;
        state.settlementResolution = null;
    };
    const pushResolutionStep = (frames: readonly PrimitiveKeyframe[]) => {
        const previousStep = steps[steps.length - 1];
        steps.push({
            ...withMomentFrame(previousStep, momentPart('composite')),
            label: 'Inspect Settlement',
            description: 'Inspect the settlement participants and resolve their cells before applying it.',
            keyframes: frames,
        });
    };
    // Settlement resolution has its own inspection step before application.
    const previewSettlement = (preview: SettlementPreview | null, into?: PrimitiveKeyframe[]): PrimitiveKeyframe[] => {
        if (preview === null) return [];
        state.preview = preview;
        state.settlementResolution = 'entry';
        const frames = [snapshot('instant')];
        for (const { resolution, durationMs } of toSettlementResolutionSchedule({ config: config.natural.display.settlementPreview, preview })) {
            state.settlementResolution = resolution;
            frames.push(snapshot({ durationMs }));
        }
        state.settlementResolution = 'all';
        if (into === undefined) pushResolutionStep(frames);
        return frames;
    };
    // The settled result already shows the Contribution-traveller, so it leaves in the same frame instead of fading over it.
    const settleTraveller = (point: NaturalTracePoint): PrimitiveKeyframe[] => {
        endPreview();
        state.machine = point.nextState;
        state.resultMaterialized = true;
        state.flight = null;
        return [snapshot('instant', [point.event])];
    };

    pushStep(
        stepMoment('initial'),
        'Start',
        args.operandSchedulingMode === 'grouped'
            ? `${operationSpec.firstLabel}, ${operationSpec.secondLabel}, and an empty ${operationSpec.resultLabel} are ready.`
            : `${operationSpec.firstLabel} and ${operationSpec.secondLabel} are visible; ${operationSpec.resultLabel} is not yet materialized.`,
        [snapshot('instant')],
    );

    if (peek()?.event.event.eventKind === 'operand-wheel-transfer-started') {
        const takeoff = consume('operand-wheel-transfer-started');
        const wheelTransfer = takeoff.nextState.operandWheelTransfer;
        if (wheelTransfer === null) throw new Error('Natural operand-wheel transfer was not created.');
        const sourcePosition = toWheelPosition({ layout, role: operationSpec.firstRole });
        state.spentBandOpacityByRole[operationSpec.firstRole] = Object.fromEntries(
            wheelTransfer.bands.map((band) => [band.power, config.natural.display.band.spentOpacity]),
        );
        state.wheelFlight = { operandWheelTransfer: wheelTransfer, position: sourcePosition, opacity: 1 };
        const liftOff = snapshot('instant', [takeoff.event]);

        state.spentBandOpacityByRole[operationSpec.secondRole] = Object.fromEntries(
            powersForValue(operationSpec.secondValue)
                .map((power) => [power, config.natural.display.band.spentOpacity]),
        );
        const clearedPath = snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 });

        const moved = consume('operand-wheel-transfer-moved');
        const movedTransfer = moved.nextState.operandWheelTransfer;
        if (movedTransfer === null) throw new Error('Natural operand-wheel transfer disappeared while moving.');
        const targetPosition = toWheelPosition({ layout, role: operationSpec.resultRole });
        state.wheelFlight = { ...state.wheelFlight, operandWheelTransfer: movedTransfer, position: targetPosition };
        const arrived = snapshot({
            durationMs: naturalFlightDurationMs({
                config,
                from: sourcePosition,
                to: targetPosition,
            }),
        }, [moved.event]);

        const settlement = consume('operand-wheel-transfer-settled-on-result');
        state.machine = settlement.nextState;
        state.resultMaterialized = true;
        state.wheelFlight = null;
        const settled = snapshot('instant', [settlement.event]);
        state.spentBandOpacityByRole[operationSpec.secondRole] = {};
        const restoredPath = snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 });

        pushStep(
            stepMoment('transfer-first-wheel'),
            `Move ${operationSpec.firstLabel}`,
            `Carry the complete ${operationSpec.firstLabel} wheel onto ${operationSpec.resultLabel} as PreResult.`,
            [liftOff, clearedPath, arrived, settled, restoredPath],
        );
    }

    while (peek()?.event.event.eventKind === 'contribution-traveller-taken-off') {
        const takeoff = consume('contribution-traveller-taken-off');
        const contributionTraveller = takeoff.nextState.contributionTraveller!;
        const role = contributionTraveller.sourceRole === operationSpec.firstRole ? 'first' : 'second';
        const label = role === 'first' ? operationSpec.firstLabel : operationSpec.secondLabel;
        const power = contributionTraveller.band.power;
        const sourcePosition = toWheelPosition({ layout, role: contributionTraveller.sourceRole });
        state.spentBandOpacityByRole[contributionTraveller.sourceRole] = {
            ...state.spentBandOpacityByRole[contributionTraveller.sourceRole],
            [power]: config.natural.display.band.spentOpacity,
        };
        // A band the source wheel never had has no ring to lift off, so it materializes instead.
        const materializes = !takeoff.previousState.wheels
            .find((wheel) => wheel.role === contributionTraveller.sourceRole)
            ?.bands.some((band) => band.power === power);
        state.flight = {
            contributionTraveller,
            position: sourcePosition,
            location: null,
            rotation: 0,
            opacity: materializes ? 0 : 1,
        };
        const takeoffFrames = [snapshot('instant', [takeoff.event])];
        if (materializes) {
            state.flight = { ...state.flight, opacity: 1 };
            takeoffFrames.push(snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 }));
        }

        const alignment = consume('contribution-traveller-aligned');
        const alignedEvent = alignment.event.event;
        if (alignedEvent.eventKind !== 'contribution-traveller-aligned') throw new Error('Unreachable alignment event.');
        state.flight = {
            ...state.flight!,
            contributionTraveller: alignment.nextState.contributionTraveller!,
        };
        const alignedPosition = toNaturalReceiverLocationPosition({
            config,
            layout,
            location: alignedEvent.location,
            sourcePosition,
        });
        const slotOffset = alignedEvent.slotOffset;
        const settlesDirectly = slotOffset === 0 && peek()?.event.event.eventKind === 'contribution-traveller-settled-on-result';
        const directPreview = settlesDirectly ? resolvePreview('contribution-traveller-settled-on-result', {
            incoming: () => flightIdentity(),
            receiver: () => ({ role: operationSpec.resultRole }),
        }) : null;
        // With nothing to preview (e.g. onto a placeholder) there is no reason to pause, so it lands in one click.
        if (settlesDirectly && directPreview === null) {
            const approach = moveFlight(alignedPosition, alignedEvent.location, [alignment.event]);
            const settlement = consume('contribution-traveller-settled-on-result');
            pushStep(
                stepMoment('fly', { role, power }),
                `Move ${label} 10^${power}`,
                `Carry the ${label} band at 10^${power} onto ${operationSpec.resultLabel}.`,
                [...takeoffFrames, approach, ...settleTraveller(settlement)],
            );
            continue;
        }
        // Otherwise it pauses at the near standoff so the preview can be read before it seats.
        const nearLocation: NaturalReceiverLocation = { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' };
        const approach = settlesDirectly
            ? moveFlight(
                toNaturalReceiverLocationPosition({ config, layout, location: nearLocation }),
                nearLocation,
                [alignment.event],
            )
            : moveFlight(alignedPosition, alignedEvent.location, [alignment.event]);

        if (slotOffset === 0 && peek()?.event.event.eventKind === 'deficit-settlement-refused') {
            const refused = consume('deficit-settlement-refused');
            const refusedFrame = snapshot('instant', [refused.event]);
            state.flight = state.flight === null ? null : { ...state.flight, opacity: 0 };
            const faded = snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 });
            state.flight = null;
            pushStep(
                stepMoment('refuse', { role, power }),
                `${label} 10^${power} cannot pay the deficit`,
                `${label} holds nothing at 10^${power}, so the band that settles onto the deficit marker `
                    + `leaves it exactly as it was, and ${operationSpec.resultLabel} is not a Natural number.`,
                [...takeoffFrames, approach, refusedFrame, faded, snapshot('instant')],
            );
            continue;
        }

        const crossingPoints: NaturalTracePoint[] = [];
        while (peek()?.event.event.eventKind === 'contribution-cell-crossed-zero') {
            crossingPoints.push(peek()!);
            pointIndex += 1;
            const assemblyIntroduction = peek()?.event.event.eventKind === 'assembly-introduced'
                ? trace.points[pointIndex++]
                : undefined;
            if (assemblyIntroduction !== undefined) crossingPoints.push(assemblyIntroduction);
        }
        pointIndex -= crossingPoints.length;
        const crossingEvents = crossingPoints.filter(
            (point) => point.event.event.eventKind === 'contribution-cell-crossed-zero',
        );
        const crossingSchedule = resolveCrossingSchedule({
            operandDigit: alignment.nextState.contributionTraveller?.band.countedSlots.length ?? 0,
            slotOffset,
            radix: state.machine.radix,
            leadSlots: config.base.animation?.crossingLeadSlots ?? 0,
        });
        const fractionByDigit = new Map(crossingSchedule.map((crossing) => [crossing.digit, crossing.fraction]));
        const rotation = toSlotRotation({ slotOffset, radix: state.machine.radix });
        const rotateDurationMs = naturalRotationDurationMs({ config, slotOffset });
        const alignKeyframes: PrimitiveKeyframe[] = [...takeoffFrames, approach];
        if (args.splitAlignmentApproach && slotOffset !== 0) {
            pushStep(stepMoment('approach', { role, power }), `Approach ${label} 10^${power}`,
                `Bring the ${label} band in front of ${operationSpec.resultLabel}, ready to turn.`,
                [...alignKeyframes]);
            alignKeyframes.length = 0;
            alignKeyframes.push(snapshot('instant'));
        }
        let turnedFraction = 0;
        const rotateTo = (fraction: number) => {
            if (fraction <= turnedFraction) return;
            state.flight = { ...state.flight!, rotation: rotation * fraction };
            alignKeyframes.push(snapshot({ durationMs: rotateDurationMs * (fraction - turnedFraction) }));
            turnedFraction = fraction;
        };

        for (let crossingIndex = 0; crossingIndex < crossingEvents.length; crossingIndex += 1) {
            const crossingPoint = consume('contribution-cell-crossed-zero');
            const crossingEvent = crossingPoint.event.event;
            if (crossingEvent.eventKind !== 'contribution-cell-crossed-zero') throw new Error('Unreachable crossing event.');
            const fraction = fractionByDigit.get(crossingEvent.digit) ?? 1;
            rotateTo(fraction);
            if (args.splitZeroCrossing && crossingIndex === 0) {
                pushStep(stepMoment('before-crossing', { role, power }), `Reach Zero at 10^${power}`,
                    `Align the incoming band up to its first Zero-crossing.`, [...alignKeyframes]);
                alignKeyframes.length = 0;
                alignKeyframes.push(snapshot('instant'));
            }
            state.flight = { ...state.flight!, contributionTraveller: crossingPoint.nextState.contributionTraveller! };
            const events = [crossingPoint.event];

            if (peek()?.event.event.eventKind === 'assembly-introduced') {
                const introduced = consume('assembly-introduced');
                state.flight = { ...state.flight!, contributionTraveller: introduced.nextState.contributionTraveller! };
                state.assembly = {
                    ...toAssemblyProjection({
                        config,
                        layout,
                        assembly: introduced.nextState.assembly!,
                        opacity: 0,
                    }),
                    entranceGrowth: 0,
                };
                events.push(introduced.event);
            }
            alignKeyframes.push(snapshot('instant', events));
            if (args.splitZeroCrossing && crossingIndex === 0) {
                pushStep(stepMoment('crossing', { role, power }), `Cross Zero at 10^${power}`,
                    `Introduce the carry Assembly at the first Zero-crossing.`, [...alignKeyframes]);
                alignKeyframes.length = 0;
                alignKeyframes.push(snapshot('instant'));
            }
        }
        if (slotOffset !== 0) rotateTo(1);
        // A assembly stays invisible until the contributionTraveller has completed its turn, then grows out of its hint ring at standoff-far.
        if (state.assembly !== null && state.assembly.radixBandOpacity === 0) {
            state.assembly = { ...state.assembly, radixBandOpacity: 1, nextBandOpacity: 1, entranceGrowth: 1 };
            alignKeyframes.push(snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 }));
            state.assembly = withoutEntrance(state.assembly);
        }

        const hadCrossings = crossingEvents.length > 0;
        const crossDescription = operation === 'addition'
            ? `A cell lands on position 0; the band rolls over and carries 1 into 10^${power + 1}.`
            : `A cell lands on position 0; the band rolls back and takes 1 from 10^${power + 1}.`;
        pushStep(
            stepMoment('align', { role, power }),
            hadCrossings ? `Cross zero at 10^${power}` : `Align ${label} 10^${power}`,
            hadCrossings
                ? crossDescription
                : slotOffset === 0
                    ? `Bring the ${label} band to 10^${power} in front of ${operationSpec.resultLabel}.`
                    : `Bring the ${label} band to 10^${power} and turn it against the ${operationSpec.resultLabel} boundary.`,
            alignKeyframes,
        );

        if (peek()?.event.event.eventKind === 'composite-formed') {
            const nearPosition = toNaturalReceiverLocationPosition({
                config,
                layout,
                location: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
            });
            const assemblyStart = state.assembly?.radixBandPosition ?? nearPosition;
            const sourceRole = state.flight?.contributionTraveller.sourceRole ?? operationSpec.secondRole;
            previewSettlement(resolvePreview('composite-formed', {
                incoming: carryAssemblyIncoming,
                // NextBand's band above the contributionTraveller is new, so it has no rotation to undo.
                receiver: (receiverPower) => ({
                    ...flightIdentity(),
                    slotOffset: receiverPower === power ? state.flight?.contributionTraveller.slotOffset ?? 0 : 0,
                }),
            }));
            if (state.assembly !== null) {
                state.assembly = {
                    ...state.assembly,
                    radixBandPosition: nearPosition,
                    nextBandPosition: nearPosition,
                    radixBandLocation: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                    nextBandLocation: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                };
            }
            const compositionFrames: PrimitiveKeyframe[] = [
                snapshot({
                    durationMs: naturalFlightDurationMs({
                        config,
                        from: assemblyStart,
                        to: nearPosition,
                    }),
                }),
            ];
            const composed = consume('composite-formed');
            endPreview();
            const assembly = composed.nextState.composite;
            if (assembly === null) throw new Error('Natural propagation assembly was not composed.');
            state.flight = null;
            state.assembly = null;
            state.composite = {
                assembly,
                sourceRole,
                position: toNaturalReceiverLocationPosition({ config, layout, location: assembly.location }),
                location: assembly.location,
                outerRotation: 0,
                opacity: 1,
                scatter: 0,
            };
            compositionFrames.push(snapshot('instant', [composed.event]));
            pushStep(
                stepMoment('compose-propagation', { role, power }),
                `Compose ${operation === 'addition' ? 'Carry' : 'Borrow'} Assembly`,
                `Move the assembly to ${operationSpec.resultLabel} and settle RadixBand onto ${label}.`,
                compositionFrames,
            );

            while (peek()?.event.event.eventKind === 'composite-outer-unit-aligned') {
                const alignmentFrames: PrimitiveKeyframe[] = [];
                const outerAlignment = consume('composite-outer-unit-aligned');
                const alignmentEvent = outerAlignment.event.event;
                if (alignmentEvent.eventKind !== 'composite-outer-unit-aligned') {
                    throw new Error('Unreachable propagation alignment event.');
                }
                const alignedAssembly = outerAlignment.nextState.composite;
                if (alignedAssembly === null || state.composite === null) {
                    throw new Error('Natural propagation assembly disappeared during alignment.');
                }
                const alignmentPosition = toNaturalReceiverLocationPosition({
                    config,
                    layout,
                    location: alignedAssembly.location,
                });
                const propagationStart = state.composite.position;
                if (propagationStart.some((coordinate, index) => coordinate !== alignmentPosition[index])) {
                    state.composite = {
                        ...state.composite,
                        position: alignmentPosition,
                        location: alignedAssembly.location,
                    };
                    alignmentFrames.push(snapshot({
                        durationMs: naturalFlightDurationMs({
                            config,
                            from: propagationStart,
                            to: alignmentPosition,
                        }),
                    }));
                }
                state.composite = {
                    ...state.composite,
                    assembly: alignedAssembly,
                    outerRotation: toSlotRotation({
                        slotOffset: alignmentEvent.slotOffset,
                        radix: state.machine.radix,
                    }),
                };
                alignmentFrames.push(snapshot(
                    alignmentEvent.slotOffset === 0
                        ? 'instant'
                        : {
                            durationMs: naturalRotationDurationMs({
                                config,
                                slotOffset: alignmentEvent.slotOffset,
                            }),
                        },
                    [outerAlignment.event],
                ));

                while (peek()?.event.event.eventKind === 'composite-outer-unit-crossed-zero') {
                    const crossing = consume('composite-outer-unit-crossed-zero');
                    const crossedAssembly = crossing.nextState.composite;
                    if (crossedAssembly === null || state.composite === null) {
                        throw new Error('Natural propagation assembly disappeared while crossing zero.');
                    }
                    state.composite = { ...state.composite, assembly: crossedAssembly };
                    alignmentFrames.push(snapshot('instant', [crossing.event]));
                }
                let introducedAssembly = false;
                const layerCompositionFrames: PrimitiveKeyframe[] = [];
                let resolutionFrames: PrimitiveKeyframe[] = [];
                if (peek()?.event.event.eventKind === 'assembly-introduced') {
                    const introduced = consume('assembly-introduced');
                    const assembly = introduced.nextState.assembly;
                    if (assembly === null) throw new Error('Natural borrow assembly was not introduced.');
                    state.assembly = { ...toAssemblyProjection({ config, layout, assembly: assembly, opacity: 0 }), entranceGrowth: 0 };
                    const farPosition = state.assembly.radixBandPosition;
                    alignmentFrames.push(snapshot('instant', [introduced.event]));
                    state.assembly = { ...state.assembly, radixBandOpacity: 1, nextBandOpacity: 1, entranceGrowth: 1 };
                    alignmentFrames.push(snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 }));
                    state.assembly = withoutEntrance(state.assembly);
                    introducedAssembly = true;
                    resolutionFrames = previewSettlement(resolvePreview('composite-formed', {
                        incoming: carryAssemblyIncoming,
                        receiver: (receiverPower) => ({
                            role: operationSpec.resultRole,
                            variant: 'flight',
                            slotOffset: receiverPower === alignmentEvent.power
                                ? state.composite?.assembly.outerUnit.slotOffset ?? 0
                                : 0,
                        }),
                    }), alignmentFrames);

                    layerCompositionFrames.push(snapshot('instant'));
                    const merged = consume('composite-formed');
                    endPreview();
                    const mergedAssembly = merged.nextState.composite;
                    if (mergedAssembly === null || state.composite === null) {
                        throw new Error('Natural propagation assembly disappeared while merging.');
                    }
                    state.assembly = {
                        ...state.assembly,
                        radixBandPosition: nearPosition,
                        nextBandPosition: nearPosition,
                        radixBandLocation: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                        nextBandLocation: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                    };
                    layerCompositionFrames.push(snapshot({
                        durationMs: naturalFlightDurationMs({
                            config,
                            from: farPosition,
                            to: nearPosition,
                        }),
                    }));
                    state.assembly = null;
                    state.composite = {
                        ...state.composite,
                        assembly: mergedAssembly,
                        outerRotation: 0,
                    };
                    layerCompositionFrames.push(snapshot('instant', [merged.event]));
                }
                if (alignmentEvent.slotOffset !== 0 || introducedAssembly) {
                    const forcedRotation = alignmentEvent.slotOffset < 0;
                    pushStep(
                        stepMoment('propagate', { role, power, targetPower: alignmentEvent.power }),
                        forcedRotation
                            ? `Force ${operation === 'addition' ? 'carry' : 'borrow'} at 10^${alignmentEvent.power}`
                            : `${operation === 'addition' ? 'Carry' : 'Borrow'} through 10^${alignmentEvent.power}`,
                        forcedRotation
                            ? `${operationSpec.resultLabel} has nothing to cancel at 10^${alignmentEvent.power}; `
                                + 'the outer Unit turns into the canonical zero slot and calls another assembly.'
                            : `${operationSpec.resultLabel} ${operation === 'addition' ? 'concatenates' : 'cancels'} the outer Unit.`,
                        alignmentFrames,
                    );
                } else if (alignmentFrames.some((keyframe) => keyframe.transition !== 'instant')) {
                    pushStep(
                        stepMoment('align-propagation', { role, power, targetPower: alignmentEvent.power }),
                        `Align ${operation === 'addition' ? 'Carry' : 'Borrow'} Assembly`,
                        `Bring the assembly in front of ${operationSpec.resultLabel} at 10^${alignmentEvent.power}.`,
                        alignmentFrames,
                    );
                } else {
                    // Nothing visibly moves, so the alignment joins the previous paused frame instead of costing a click.
                    amendLastStep((keyframes) => [...keyframes, ...alignmentFrames]);
                }
                if (resolutionFrames.length > 0) pushResolutionStep(resolutionFrames);
                if (introducedAssembly) {
                    pushStep(
                        stepMoment('compose-propagation', { role, power, targetPower: alignmentEvent.power }),
                        `Compose ${operation === 'addition' ? 'Carry' : 'Borrow'} Assembly`,
                        `Move the assembly to ${operationSpec.resultLabel} and merge RadixBand `
                            + `onto the outer Unit at 10^${alignmentEvent.power}.`,
                        layerCompositionFrames,
                    );
                }
            }

            if (peek()?.event.event.eventKind === 'operation-failed') {
                const shatterFrames: PrimitiveKeyframe[] = [snapshot('instant')];
                state.composite = { ...state.composite, scatter: 1, opacity: 0 };
                shatterFrames.push(snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 }));
                state.composite = null;
                shatterFrames.push(snapshot('instant'));
                pushStep(
                    stepMoment('settle-propagation', { role, power }),
                    `Shatter ${operation === 'addition' ? 'Carry' : 'Borrow'} Assembly`,
                    `The ${operation === 'addition' ? 'carry' : 'borrow'} stream never closes, so the assembly `
                        + `shatters and ${operationSpec.resultLabel} is not a Natural number.`,
                    shatterFrames,
                );
                continue;
            }

            const receiverPosition = toNaturalReceiverLocationPosition({
                config,
                layout,
                location: { receiverRole: operationSpec.resultRole, locationKind: 'receiver' },
            });
            const propagationStart = state.composite.position;
            const lowerMemberCount = state.composite.assembly.lowerBands.length;
            const lowerRole = state.composite.sourceRole;
            previewSettlement(resolvePreview('composite-settled', {
                incoming: (index) => ({
                    role: index < lowerMemberCount ? lowerRole : operationSpec.resultRole,
                    variant: 'flight',
                }),
                receiver: () => ({ role: operationSpec.resultRole }),
            }));
            state.composite = {
                ...state.composite,
                position: receiverPosition,
                location: { receiverRole: operationSpec.resultRole, locationKind: 'receiver' },
            };
            const settlementFrames: PrimitiveKeyframe[] = [
                snapshot({
                    durationMs: naturalFlightDurationMs({
                        config,
                        from: propagationStart,
                        to: receiverPosition,
                    }),
                }),
            ];
            const settlement = consume('composite-settled');
            endPreview();
            state.machine = settlement.nextState;
            state.resultMaterialized = true;
            state.composite = null;
            settlementFrames.push(snapshot('instant', [settlement.event]));
            pushStep(
                stepMoment('settle-propagation', { role, power }),
                `Settle ${operation === 'addition' ? 'Carry' : 'Borrow'} Assembly`,
                `Settle every resolved assembly band onto ${operationSpec.resultLabel} atomically.`,
                settlementFrames,
            );
            continue;
        }

        if (peek()?.event.event.eventKind === 'deficit-resolution-requested') {
            const request = consume('deficit-resolution-requested');
            const nearPosition = toNaturalReceiverLocationPosition({
                config,
                layout,
                location: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
            });
            if (peek()?.event.event.eventKind === 'deficit-discharged') {
                previewSettlement(resolvePreview('deficit-discharged', {
                    incoming: () => flightIdentity(),
                    receiver: () => ({ role: operationSpec.resultRole, variant: 'marker' }),
                }));
            }
            const settleMarker = moveFlight(
                nearPosition,
                { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                [request.event],
            );
            if (peek()?.event.event.eventKind === 'deficit-propagated') {
                const propagated = consume('deficit-propagated');
                state.flight = state.flight === null || propagated.nextState.contributionTraveller === null
                    ? state.flight
                    : { ...state.flight, contributionTraveller: propagated.nextState.contributionTraveller };
                if (propagated.nextState.assembly !== null) {
                    state.assembly = {
                        ...toAssemblyProjection({
                            config,
                            layout,
                            assembly: propagated.nextState.assembly,
                            opacity: 1,
                        }),
                        radixBandOpacity: 0,
                    };
                }
                const propagatedFrame = snapshot('instant', [propagated.event]);
                state.flight = state.flight === null ? null : { ...state.flight, opacity: 0 };
                const faded = snapshot({ durationMs: config.base.animation?.fadeDurationMs ?? 0 });
                state.flight = null;
                pushStep(
                    stepMoment('settle', { role, power }),
                    `Propagate deficit through ${label} 10^${power}`,
                    `The uncounted ${label} band becomes 9 and passes the deficit to 10^${power + 1}.`,
                    [settleMarker, propagatedFrame, faded, snapshot('instant')],
                );
                continue;
            }

            const discharged = consume('deficit-discharged');
            endPreview();
            // The paid marker survives as the composite: the flight takes over its band keys.
            state.assembly = null;
            if (state.flight !== null && discharged.nextState.contributionTraveller !== null) {
                state.flight = {
                    ...state.flight,
                    contributionTraveller: discharged.nextState.contributionTraveller,
                    identity: { role: operationSpec.resultRole, variant: 'marker' },
                };
            }
            pushStep(
                stepMoment('settle', { role, power }),
                `Settle ${label} onto the deficit marker 10^${power + 1}`,
                `${label} pays the waiting deficit before settling onto ${operationSpec.resultLabel}.`,
                [settleMarker, snapshot('instant', [discharged.event])],
            );
        }

        if (peek()?.event.event.eventKind === 'assembly-settled-on-operand') {
            const nearPosition = toNaturalReceiverLocationPosition({
                config,
                layout,
                location: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
            });
            const from = state.assembly?.radixBandPosition ?? nearPosition;
            previewSettlement(resolvePreview('assembly-settled-on-operand', {
                incoming: () => ({ role: operationSpec.firstRole, variant: 'radix-band' }),
                receiver: () => ({ ...flightIdentity(), slotOffset: state.flight?.contributionTraveller.slotOffset ?? 0 }),
            }));
            if (state.assembly !== null) {
                state.assembly = {
                    ...state.assembly,
                    radixBandPosition: nearPosition,
                    nextBandPosition: nearPosition,
                    radixBandLocation: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                    nextBandLocation: { receiverRole: operationSpec.resultRole, locationKind: 'standoff-near' },
                };
            }
            const seatRadixBand = snapshot({
                durationMs: naturalFlightDurationMs({ config, from, to: nearPosition }),
            });
            const previousAssembly = state.assembly;
            const settled = consume('assembly-settled-on-operand');
            endPreview();
            state.flight = { ...state.flight!, contributionTraveller: settled.nextState.contributionTraveller!, composed: true };
            if (previousAssembly !== null && settled.nextState.assembly !== null) {
                state.assembly = {
                    ...previousAssembly,
                    assembly: { ...settled.nextState.assembly, radixBand: null },
                };
            }
            const compositeMutation = snapshot('instant', [settled.event]);
            pushStep(
                stepMoment('radix-band', { role, power }),
                `RadixBand 10^${power}`,
                `RadixBand settles onto the ${label} contributionTraveller, toggling every non-overhang cell.`,
                [seatRadixBand, compositeMutation],
            );
        }

        const receiverPosition = toNaturalReceiverLocationPosition({
            config,
            layout,
            location: { receiverRole: operationSpec.resultRole, locationKind: 'receiver' },
        });
        previewSettlement(resolvePreview('contribution-traveller-settled-on-result', {
            incoming: () => flightIdentity(),
            receiver: () => ({ role: operationSpec.resultRole }),
        }));
        const seat = moveFlight(receiverPosition, { receiverRole: operationSpec.resultRole, locationKind: 'receiver' });
        const settlement = consume('contribution-traveller-settled-on-result');
        const retainedAssembly = settlement.nextState.assembly === null ? null : state.assembly;
        state.assembly = retainedAssembly;
        const landing = [seat, ...settleTraveller(settlement)];
        const resultBand = settlement.nextState.wheels
            .find((wheel) => wheel.role === operationSpec.resultRole)
            ?.bands.find((band) => band.power === power);

        pushStep(
            stepMoment('merge', { role, power }),
            `Apply ${label} 10^${power}`,
            `${role === 'first' || operation === 'addition' ? 'Add' : 'Remove'} `
                + `${contributionTraveller.band.countedSlots.length} at 10^${power}; the band reads ${resultBand?.countedSlots.length ?? 0}.`,
            landing,
        );
    }

    const normalization = peek()?.event.event.eventKind === 'result-normalized'
        ? consume('result-normalized')
        : undefined;

    if (peek()?.event.event.eventKind === 'operation-failed') {
        const failure = consume('operation-failed');
        const failureEvent = failure.event.event;
        if (failureEvent.eventKind !== 'operation-failed') throw new Error('Unreachable failure event.');
        state.resultMaterialized = true;
        const failureWheels = snapshotWheels({ config, layout, operationSpec, state });
        const lastStep = steps[steps.length - 1];
        const lastKeyframe = lastStep.keyframes[lastStep.keyframes.length - 1];
        steps[steps.length - 1] = {
            ...lastStep,
            keyframes: [
                ...lastStep.keyframes.slice(0, -1),
                {
                    ...lastKeyframe,
                    wheels: failureWheels,
                    events: [...lastKeyframe.events, failure.event],
                },
            ],
        };

        if (pointIndex !== trace.points.length) {
            throw new Error(`Natural presentation consumed ${pointIndex} of ${trace.points.length} trace points.`);
        }
        return {
            steps,
            outcome: { outcomeKind: 'failed', failureKind: failureEvent.failureKind },
        };
    }

    const completion = consume('operation-completed');
    state.resultMaterialized = true;
    const resultValue = state.machine.wheels
        .find((wheel) => wheel.role === operationSpec.resultRole)
        ?.bands.reduce(
            (value, band) => value + band.countedSlots.length * (state.machine.radix ** band.power),
            0,
        ) ?? 0;
    const completionWheels = snapshotWheels({ config, layout, operationSpec, state });
    const terminalWheels = dimStableWheelRoles({
        config,
        wheels: completionWheels,
        roles: [operationSpec.firstRole, operationSpec.secondRole],
    });
    const terminalDimmingNeeded = args.dimTerminalOperands !== false && completionWheels.some((wheel) => (
        wheel.variant === undefined
        && (wheel.role === operationSpec.firstRole || wheel.role === operationSpec.secondRole)
        && wheel.powers.some((power) => (
            wheel.bandOpacityByPower?.[power] !== config.natural.display.band.spentOpacity
        ))
    ));
    const terminalDimmingKeyframes: PrimitiveKeyframe[] = terminalDimmingNeeded
        ? [{
            wheels: terminalWheels,
            transition: { durationMs: config.base.animation?.fadeDurationMs ?? 0 },
            events: [],
        }]
        : [];
    if (normalization !== undefined) {
        pushStep(
            stepMoment('complete'),
            'Complete',
            `${operationSpec.firstValue} ${operation === 'addition' ? '+' : '-'} ${operationSpec.secondValue} = ${resultValue}.`,
            [
                {
                    wheels: completionWheels,
                    transition: 'instant',
                    events: [normalization.event, completion.event],
                },
                ...terminalDimmingKeyframes,
            ],
        );
    } else {
        const lastStep = steps[steps.length - 1];
        const lastKeyframe = lastStep.keyframes[lastStep.keyframes.length - 1];
        steps[steps.length - 1] = {
            ...lastStep,
            keyframes: [
                ...lastStep.keyframes.slice(0, -1),
                {
                    ...lastKeyframe,
                    wheels: completionWheels,
                    events: [...lastKeyframe.events, completion.event],
                },
                ...terminalDimmingKeyframes,
            ],
        };
    }

    if (pointIndex !== trace.points.length) {
        throw new Error(`Natural presentation consumed ${pointIndex} of ${trace.points.length} trace points.`);
    }
    return { steps, outcome: { outcomeKind: 'completed' } };
}

export function deriveNaturalAdditionSubtractionStepStates(args: {
    config: Config;
    operation: AdditionSubtractionOperation;
    operandOne: number;
    operandTwo: number;
    options?: PrimitiveOperationDerivationOptions;
}): NaturalStepDerivationResult {
    const trace = deriveNaturalTrace({
        operation: args.operation,
        radix: args.config.natural.radix,
        operandOne: args.operandOne,
        operandTwo: args.operandTwo,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
    }, {
        allowExoticTerminalStates: args.options?.allowExoticTerminalStates,
        runawayBorrowAssemblyBudget: args.config.natural.runawayBudget.borrowAssemblies,
    });
    return projectNaturalTraceToStepStates({
        ...args,
        operandSchedulingMode: args.config.natural.operandSchedulingMode,
        trace,
    });
}